<script setup>
import {ref, onMounted, inject} from 'vue'
import {useRouter} from 'vue-router'
import axios from 'axios'

const router = useRouter()
const backEndURL = process.env.VUE_APP_BACKEND_URL

const contentData = inject('contentData')
const contentFileName = process.env.VUE_APP_CONTENT_FILE_NAME

const navItems = ref([])
const isNavOpen = ref(false)

onMounted(async () => {
    await getNavigationItems()
})

async function getNavigationItems() {
    try {
        const res = await axios.get(`${backEndURL}/api/navigation-items`)
        navItems.value = res.data
    } catch (err) {
        console.error(err)
        alert('Error loading navigation items')
    }
}

function handleNavClick(href) {
    isNavOpen.value = false
}

const handleBurgerClick = () => {
    isNavOpen.value = !isNavOpen.value
}

const openMenuIndex = ref(null)

function toggleMenu(idx) {
    openMenuIndex.value = openMenuIndex.value === idx ? null : idx
}
</script>

<template>
    <!-- TOP NAVBAR -->
    <nav class="py-2 bg-white top-0 z-50">
        <div class="container mx-auto">
            <div class="relative flex items-center justify-between">
                <!-- LOGO -->
                <div class="w-auto">
                    <a class="inline-block" href="#">
                        <img :src="contentData.left_logo" alt="Logo" width="120" height="40" />
                    </a>
                </div>

                <!-- DESKTOP NAV LINKS  -->
                <div
                    class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 hidden xl:block">
                    <ul class="flex items-center">
                        <li
                            v-for="(item, idx) in navItems.filter(i => i.display)"
                            :key="item.id"
                            class="relative font-heading mr-12 text-base w-auto">
                            <a
                                :target="item.href.charAt(0) === '#' ? '_self' : '_blank'"
                                v-if="!item.submenu_items?.length"
                                class="hover:text-blue-600 cursor-pointer"
                                :href="item.href">
                                {{ item.title }}
                            </a>

                            <template v-else>
                                <a class="hover:text-blue-600 cursor-pointer" @click="toggleMenu(idx)">
                                    {{ item.title }}
                                </a>

                                <ul
                                    class="absolute top-full left-0 mt-2 bg-white border shadow-md rounded-md z-50"
                                    :class="openMenuIndex === idx ? 'block' : 'hidden'">
                                    <li
                                        v-for="[subTitle, subHref] in item.submenu_items"
                                        :key="subHref"
                                        class="whitespace-nowrap px-4 py-2 hover:bg-blue-100 z-50">
                                        <a
                                            :target="subHref.charAt(0) === '#' ? '_self' : '_blank'"
                                            :href="subHref"
                                            @click="
                                                () => {
                                                    handleNavClick(subHref)
                                                }
                                            ">
                                            {{ subTitle }}
                                        </a>
                                    </li>
                                </ul>
                            </template>
                        </li>
                    </ul>
                </div>

                <!-- RIGHT-SIDE BUTTON (Register/Contact) -->
                <div class="w-auto hidden xl:block" v-if="contentData.what === 'rn'">
                    <a
                        :class="contentData.what === 'rn' ? 'bg-green-500' : 'bg-red-900'"
                        class="inline-block py-4 px-8 font-heading font-medium text-base text-white border hover:border-green-600 rounded-sm transition duration-200"
                        :href="contentData.what === 'rn' ? '#contact' : '#contact'">
                        {{ contentData.what === 'rn' ? 'Contact' : 'Contact' }}
                    </a>
                    <!--                    TODO change to register button for pht later !!!!!!!!!!!!!!!!!!-->
                </div>
                <div class="w-auto hidden xl:block" v-else>
                    <a
                        class="bg-red-900 inline-block py-4 px-8 font-heading font-medium text-base text-white border hover:border-green-600 rounded-sm transition duration-200"
                        href="https://ethzurich.eventsair.com/pht26/regi">
                        Register
                    </a>
                </div>

                <!-- BURGER ICON (MOBILE) -->
                <div class="w-auto xl:hidden">
                    <button
                        class="inline-flex w-14 h-14 justify-center items-center bg-gray-500 hover:bg-gray-600 rounded-full"
                        @click="handleBurgerClick">
                        <svg
                            width="20"
                            height="10"
                            viewBox="0 0 20 10"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M19 9H1M19 1H1"
                                stroke="white"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    </nav>

    <!-- SIDE NAV (MOBILE MENU) -->

    <div v-if="isNavOpen" class="navbar-menu fixed top-0 left-0 bottom-0 w-5/6 max-w-sm z-50">
        <div
            class="navbar-backdrop fixed inset-0 backdrop-blur-xl backdrop-filter bg-gray-900 bg-opacity-80"
            @click="isNavOpen = false"></div>

        <nav class="relative pt-7 pb-8 bg-white h-full overflow-y-auto">
            <div class="flex flex-col px-6 h-full">
                <ul class="w-full mb-auto pb-16">
                    <li v-for="item in navItems.filter(i => i.display)" :key="item.id">
                        <a
                            class="font-heading block text-base font-medium py-4 px-6 hover:bg-green-50 rounded-sm"
                            :href="item.href"
                            @click="isNavOpen = false"
                            v-if="!item.submenu_items?.length">
                            {{ item.title }}
                        </a>

                        <div v-else>
                            <div class="font-heading block text-base font-medium py-4 px-6 text-gray-900">
                                {{ item.title }}
                            </div>
                            <ul class="pl-6">
                                <li v-for="[subTitle, subHref] in item.submenu_items" :key="subHref">
                                    <a
                                        class="block text-sm py-2 px-6 text-gray-700 hover:bg-green-50 rounded-sm"
                                        :href="subHref"
                                        @click="isNavOpen = false">
                                        {{ subTitle }}
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </li>
                </ul>
            </div>
        </nav>
    </div>
</template>

<style scoped>
nav {
    position: relative;
    z-index: 100;
    overflow: visible;
}
</style>
